import React, { useState } from "react";
import axios from "axios";
import { Footer } from "../common";
import "../../styles/ContactScreen.css";
import { toast } from "react-toastify";

const REASONS = [
  "General Enquiry",
  "Order Issue",
  "Return / Refund",
  "Product Question",
  "Shipping & Delivery",
  "Account & Billing",
  "Feedback / Suggestion",
  "Other",
];

const ContactScreen = () => {
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    email: "",
    reason: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Validate reason selected
    if (!formData.reason) {
      toast.error("Please select a reason for contact.");
      return;
    }

    setSubmitting(true);
    try {
      // Combine reason + message into reason_of_contact
      const reasonOfContact = formData.message.trim()
        ? `${formData.reason}: ${formData.message.trim()}`
        : formData.reason;

      const payload = {
        name: formData.name,
        address: formData.address,
        email: formData.email,
        reason_of_contact: reasonOfContact, // ✅ correct field name per API
      };

      const base = process.env.REACT_APP_BASE_URL;
      const url = `${base}/api/customer/contacts`; // ✅ correct endpoint per API

      const request = axios.post(url, payload);
      await toast.promise(request, {
        loading: "Sending message…",
        success: "Message sent. We'll reply soon!",
        error: (err) =>
          err?.response?.data?.message || "Sending failed. Please try again.",
      });

      setFormData({
        name: "",
        address: "",
        email: "",
        reason: "",
        message: "",
      });
    } catch (err) {
      console.error("[CONTACT] submit failed:", err);
      toast.error(
        "Sorry, there was a problem sending the message. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Icons ── */
  const EmailIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="5" width="18" height="14" rx="3" stroke="currentColor" strokeWidth="2" />
      <path d="M3 7l9 7 9-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const LocationIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M12 22s-7-4.5-7-11a7 7 0 0114 0c0 6.5-7 11-7 11z" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="11" r="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
  const PhoneIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.86 19.86 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.86 19.86 0 012.08 4.2 2 2 0 014.08 2h3a2 2 0 012 1.72c.12.86.33 1.7.64 2.5a2 2 0 01-.45 2.11l-1.27 1.27a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.8.31 1.64.52 2.5.64A2 2 0 0122 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const SendIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
  const ChevronIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );

  const contactInfo = [
    {
      icon: <EmailIcon />,
      label: "Email Us",
      value: "easyworldonline101@gmail.com",
      color: "orange",
    },
    {
      icon: <LocationIcon />,
      label: "Our Location",
      value: "287 Liverpool Street, Hobart, TASMANIA, 7000",
      color: "navy",
    },
    {
      icon: <PhoneIcon />,
      label: "Contact Us",
      value: "+61 412 345 678",
      color: "orange",
    },
  ];

  const faqs = [
    {
      q: "How long does a reply take?",
      a: "We typically respond within 24 hours on business days.",
    },
    {
      q: "Can I track my order here?",
      a: "Yes — include your order number and we'll pull it up straight away.",
    },
    {
      q: "Do you offer live chat?",
      a: "Live chat is available inside the app during business hours.",
    },
  ];

  return (
    <div className="ct-root">
      {/* ── Hero banner ── */}
      <section className="ct-hero">
        <div className="ct-hero-bg">
          <div className="ct-hero-orb ct-orb1" />
          <div className="ct-hero-orb ct-orb2" />
          <div className="ct-hero-grid" />
        </div>
        <div className="ct-hero-body">
          <div className="ct-hero-eyebrow">Get In Touch</div>
          <h1 className="ct-hero-title">
            We'd love to <span className="ct-accent">hear from you</span>
          </h1>
          <p className="ct-hero-sub">
            Questions, feedback, or need help with an order? Send us a message
            and we'll get back to you fast.
          </p>
        </div>
      </section>

      {/* ── Contact cards ── */}
      <section className="ct-cards-strip">
        <div className="ct-cards-inner">
          {contactInfo.map((item) => (
            <div key={item.label} className={`ct-info-card ct-info-card--${item.color}`}>
              <div className="ct-info-icon">{item.icon}</div>
              <div className="ct-info-text">
                <span className="ct-info-label">{item.label}</span>
                <span className="ct-info-value">{item.value}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Main content ── */}
      <section className="ct-main">
        <div className="ct-main-inner">
          {/* Form card */}
          <div className="ct-form-card">
            <div className="ct-form-header">
              <h2 className="ct-form-title">Send a Message</h2>
              <p className="ct-form-sub">
                Fill in the details below and we'll be in touch.
              </p>
            </div>

            <form className="ct-form" onSubmit={handleSubmit}>

              {/* Row 1 — Name + Email */}
              <div className="ct-form-row">
                <div className="ct-field">
                  <label className="ct-label" htmlFor="name">
                    Full Name <span className="ct-req">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="ct-input"
                    placeholder="Your full name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="ct-field">
                  <label className="ct-label" htmlFor="email">
                    Email Address <span className="ct-req">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="ct-input"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              {/* Row 2 — Address (full width) */}
              <div className="ct-field">
                <label className="ct-label" htmlFor="address">
                  Address <span className="ct-req">*</span>
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  className="ct-input"
                  placeholder="Street, City, State, Postcode"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* Row 3 — Reason dropdown */}
              <div className="ct-field">
                <label className="ct-label" htmlFor="reason">
                  Reason for Contact <span className="ct-req">*</span>
                </label>
                <div className="ct-select-wrap">
                  <select
                    id="reason"
                    name="reason"
                    className="ct-input ct-select"
                    value={formData.reason}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="" disabled>Select a reason…</option>
                    {REASONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <span className="ct-select-chevron">
                    <ChevronIcon />
                  </span>
                </div>
              </div>

              {/* Row 4 — Message (full width) */}
              <div className="ct-field">
                <label className="ct-label" htmlFor="message">
                  Message <span className="ct-req">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  className="ct-textarea"
                  placeholder="Tell us how we can help you…"
                  rows={5}
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <button type="submit" className="ct-submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <span className="ct-submit-spinner" />
                    <span>Sending…</span>
                  </>
                ) : (
                  <>
                    <SendIcon />
                    <span>Send Message</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Right col: map + FAQ */}
          <div className="ct-right-col">
            {/* Map */}
            <div className="ct-map-card">
              <div className="ct-map-header">
                <LocationIcon />
                <span>287 Liverpool Street, Hobart, Tasmania 7000</span>
              </div>
              <div className="ct-map-wrap">
                <iframe
                  title="Easy World Hobart Map"
                  className="ct-map-iframe"
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src="https://www.google.com/maps?q=287%20Liverpool%20Street%20Hobart%20Tasmania%207000&z=16&output=embed"
                />
              </div>
            </div>

            {/* FAQ */}
            <div className="ct-faq-card">
              <h3 className="ct-faq-title">Quick Answers</h3>
              <div className="ct-faq-list">
                {faqs.map((f) => (
                  <div key={f.q} className="ct-faq-item">
                    <div className="ct-faq-q">{f.q}</div>
                    <div className="ct-faq-a">{f.a}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactScreen;